import * as Sentry from '@sentry/react'
import { isChunkLoadErrorBeingRecovered } from 'utils/initChunkLoadErrorRecovery'
import { appInfo } from './appInfo'

/**
 * Error messages that are not bugs and have no fix on our side.
 *
 * `ignoreErrors` matches a **plain string as a substring** (`isMatchingPattern`
 * does `value.indexOf(pattern) !== -1`), against both the exception value and
 * `` `${type}: ${value}` ``. So a bare 'Failed to fetch' also swallows
 * 'Failed to fetch dynamically imported module: …' — a real, fixable bug
 * (BANYAN_APP-SC / DEV-5179).
 *
 * That is why the two network entries below are anchored regexes rather than
 * strings. Keep them that way, and prefer an anchored regex for anything whose
 * message is a prefix of a longer, more specific error.
 */
const IGNORED_ERROR_MESSAGES = [
  // Plain network failures: bad connections, cancelled navigations,
  // backgrounded mobile tabs. Nothing to fix in the app.
  // Anchored — see the note above before adding to this group.
  /^(TypeError: )?Failed to fetch$/,
  // Safari's phrasing of the same thing.
  /^(TypeError: )?Load failed$/,
  /^(TypeError: )?The network connection was lost\.?$/,
  'TypeError: NetworkError',
  // Our own control-flow class, thrown by apiFetch to signal offline/timeout
  // states the app already handles. See utils/errorCodes.ts.
  'OfflineRequestError',
  // Benign spec-level browser noise, fires on any resize-observing layout.
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Outlook's SafeLink crawler prefetching links, not a real user session.
  'Object Not Found Matching Id',
  // Desktop browsers without Web Bluetooth. An environmental fact with no
  // user-facing remedy, so filtering is the permanent answer rather than a
  // stopgap. Note that BLE *permission denied* is deliberately NOT filtered --
  // that one is actionable and is tracked on DEV-5360.
  'Web Bluetooth API not available',
  // iOS kCLErrorLocationUnknown: the device could not get a fix, typically
  // indoors. Transient and not actionable. Matching the code rather than the
  // surrounding sentence avoids depending on the curly apostrophe in
  // "The operation couldn't be completed."
  // 'kCLErrorDomain error 1' is kCLErrorDenied and is NOT filtered -- see
  // DEV-5360, the permission guard in CurrentLocation misses it today.
  'kCLErrorDomain error 0',
  /^Non-Error promise rejection captured/,
  // Browser extensions injecting into the page.
  /chrome-extension:/,
  /moz-extension:/,
  /safari-extension:/,
  /Extension context invalidated/,
]

if (appInfo.useSentry && appInfo.sentryEnvironment !== 'local') {
  Sentry.init({
    dsn: 'https://eb10a544960b47ecb104923265b0293a@o117841.ingest.us.sentry.io/4504058028621824',
    environment: appInfo.sentryEnvironment,
    // Sentry 10 rewrites fetch's own TypeError to "Failed to fetch (<host>)" by default, which
    // breaks the anchored ignoreErrors entries and getAuthRequestFailure's unreachable check.
    enhanceFetchErrorMessages: false,
    ignoreErrors: IGNORED_ERROR_MESSAGES,
    beforeSend: (event, hint) => (isChunkLoadErrorBeingRecovered(hint.originalException) ? null : event),
    initialScope: {
      tags: appInfo,
    },
  })
}
